import { AxiosInstance, AxiosRequestConfig } from "axios";

import { AxiosInstanceInterceptorRequestExtensionI } from "@/api/interceptors/interface";

/**
 * Team → Analytics (C-14 phase 2, W2): C-11's workspace analytics, Command
 * Center's Pipeline funnel and meeting outcomes by campaign and rep, on the
 * role zone (/user/v1/manage/workspace/:id/...) for an Admin or the
 * workspace's LGA. Both routes exist only while glen's
 * `user_roles_analytics_enabled` (and `user_roles_enabled`) is on; otherwise
 * they answer 404 with error 1035. Dates are ET calendar days
 * (`YYYY-MM-DD`, inclusive). The backend computes every count and rate; a
 * rate is a fraction 0..1, or null when its denominator is 0.
 */

export const WORKSPACE_ANALYTICS_GRANULARITIES = {
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
} as const;

export type WorkspaceAnalyticsGranularityI =
  (typeof WORKSPACE_ANALYTICS_GRANULARITIES)[keyof typeof WORKSPACE_ANALYTICS_GRANULARITIES];

export const WORKSPACE_ANALYTICS_SEGMENTS = {
  CAMPAIGN: "campaign",
  REP: "rep",
} as const;

export type WorkspaceAnalyticsSegmentI =
  (typeof WORKSPACE_ANALYTICS_SEGMENTS)[keyof typeof WORKSPACE_ANALYTICS_SEGMENTS];

export interface WorkspaceAnalyticsRatesI {
  connect_rate: number | null;
  conversation_rate: number | null;
  icp_rate: number | null;
  book_rate: number | null;
  show_rate: number | null;
  qualify_rate: number | null;
  bm_to_qm: number | null;
}

export interface WorkspaceAnalyticsStatsI {
  dials: number;
  connects: number;
  conversations: number;
  convo_right_icp: number;
  booked_meetings: number;
  qualified_meetings: number;
  show_meetings: number;
  lost_meetings: number;
  fumbled_meetings: number;
  rates: WorkspaceAnalyticsRatesI;
}

// One bucket, clipped to the window: an ET day, Monday week or calendar month.
export interface WorkspaceAnalyticsPeriodI extends WorkspaceAnalyticsStatsI {
  period_start: string;
  period_end: string;
}

export interface WorkspaceAnalyticsRepI {
  user_id: string;
  name: string;
  // The rep's workspace is this one.
  on_roster: boolean;
  // The one "Removed users" row every deleted user is summed into, with
  // user_id "" (C14-W2-D86): key it by this, not by user_id.
  removed?: boolean;
  stats: WorkspaceAnalyticsStatsI;
  // Only on the top-level `reps` with `segment=rep`.
  series?: WorkspaceAnalyticsPeriodI[];
}

export interface WorkspaceAnalyticsCampaignI {
  campaign_id: string;
  campaign_name: string;
  stats: WorkspaceAnalyticsStatsI;
  reps: WorkspaceAnalyticsRepI[];
  // Only with `segment=campaign`.
  series?: WorkspaceAnalyticsPeriodI[];
}

export interface WorkspaceAnalyticsWindowI {
  from: string;
  to: string;
  granularity?: WorkspaceAnalyticsGranularityI;
  time_zone: string;
}

export interface GetWorkspaceAnalyticsResponseI {
  window: WorkspaceAnalyticsWindowI;
  // The oldest refresh among the scoped campaigns' rollups; null if never.
  updated_at: string | null;
  totals: WorkspaceAnalyticsStatsI;
  series: WorkspaceAnalyticsPeriodI[];
  campaigns: WorkspaceAnalyticsCampaignI[];
  reps: WorkspaceAnalyticsRepI[];
}

export interface GetWorkspaceAnalyticsParamsI {
  from: string;
  to: string;
  granularity: WorkspaceAnalyticsGranularityI;
  campaign_ids?: string[];
  user_ids?: string[];
  segment?: WorkspaceAnalyticsSegmentI;
}

export const DATA_HEALTH_STATUSES = {
  OK: "ok",
  WARN: "warn",
  ALERT: "alert",
} as const;

export type DataHealthStatusI =
  (typeof DATA_HEALTH_STATUSES)[keyof typeof DATA_HEALTH_STATUSES];

export const DATA_HEALTH_CHECK_KEYS = {
  FRESHNESS: "freshness",
  UNDISPOSITIONED_CALLS: "undispositioned_calls",
  UNCLASSIFIED_DISPOSITIONS: "unclassified_dispositions",
  BAD_DATA: "bad_data",
  OFF_ROSTER_REPS: "off_roster_reps",
  MEETINGS_MISSING_OUTCOME: "meetings_missing_outcome",
} as const;

export type DataHealthCheckKeyI =
  (typeof DATA_HEALTH_CHECK_KEYS)[keyof typeof DATA_HEALTH_CHECK_KEYS];

export interface DataHealthOffRosterRepI {
  user_id: string;
  name: string;
  // Every deleted user, summed (C14-W2-D86); user_id is "".
  removed?: boolean;
  dials: number;
  booked_meetings: number;
}

/**
 * One check. Keys are append-only on the backend: an unknown key is ignored.
 * `count`/`of`/`share` are there for the share checks, `updated_at`,
 * `minutes_old` and `backfill_complete` for freshness, and `reps` for
 * off_roster_reps.
 */
export interface DataHealthCheckI {
  key: string;
  status: DataHealthStatusI;
  count?: number;
  of?: number;
  // count ÷ of as a fraction; null when `of` is 0 (C11-D3).
  share?: number | null;
  // Freshness: null if any scoped campaign was never refreshed.
  updated_at?: string | null;
  minutes_old?: number | null;
  // Freshness: false while a scoped campaign's backfill hasn't reached `from`.
  backfill_complete?: boolean;
  reps?: DataHealthOffRosterRepI[];
}

export interface DataHealthCampaignI {
  campaign_id: string;
  campaign_name: string;
  checks: DataHealthCheckI[];
}

export interface GetWorkspaceAnalyticsDataHealthResponseI {
  window: WorkspaceAnalyticsWindowI;
  checks: DataHealthCheckI[];
  campaigns: DataHealthCampaignI[];
}

export interface GetWorkspaceAnalyticsDataHealthParamsI {
  from: string;
  to: string;
  campaign_ids?: string[];
}

/**
 * One meeting the "meetings missing an outcome" check counts (C14-W2-D88):
 * passed, no outcome yet. `meeting_date` and `booked_at` are RFC 3339
 * instants; `booker_name` is "Removed user" for a deleted rep.
 */
export interface WorkspaceAnalyticsMissingOutcomeI {
  campaign_id: string;
  campaign_name: string;
  account_id: string;
  account_name: string;
  contact_id: string;
  prospect_name: string;
  booker_user_id: string;
  booker_name: string;
  meeting_date: string;
  booked_at: string;
}

export interface GetWorkspaceAnalyticsMissingOutcomesResponseI {
  window: WorkspaceAnalyticsWindowI;
  // The last ET day counted: the window's end, or 3 days before today.
  through: string;
  // Equals the check's count for the same window and campaigns.
  total: number;
  page: number;
  page_size: number;
  meetings: WorkspaceAnalyticsMissingOutcomeI[];
}

export interface GetWorkspaceAnalyticsMissingOutcomesParamsI {
  from: string;
  to: string;
  campaign_ids?: string[];
  // 1-based; the backend clamps page_size to 1–100.
  page: number;
  page_size: number;
}

/**
 * The query string goes into the URL itself (not axios `params`), as the
 * customer app built it (C-11), so both apps send the same request.
 */
export const toWorkspaceAnalyticsQuery = (
  params: Record<string, string | string[] | undefined>
) => {
  const search = new URLSearchParams();

  Object.keys(params).forEach((key) => {
    const value = params[key];

    if (Array.isArray(value)) {
      if (value.length > 0) {
        search.set(key, value.join(","));
      }

      return;
    }

    if (value) {
      search.set(key, value);
    }
  });

  return search.toString();
};

// A 404 is the switch being off: an answer the page acts on, not an error to
// log on every load. Built per call: the request interceptor
// writes into `meta`.
const switchProbeConfig = () =>
  ({
    meta: { expectedStatuses: [404] },
  }) as AxiosRequestConfig & AxiosInstanceInterceptorRequestExtensionI;

// The backend refuses longer id lists (1235).
export const MAX_FILTER_IDS = 200;

export const WorkspaceAnalyticsAPIEndpoints = (api: AxiosInstance) => ({
  getWorkspaceAnalytics: (
    workspaceId: string,
    { campaign_ids, user_ids, ...params }: GetWorkspaceAnalyticsParamsI
  ) =>
    api.get<GetWorkspaceAnalyticsResponseI>(
      `/v1/manage/workspace/${encodeURIComponent(workspaceId)}/analytics?${toWorkspaceAnalyticsQuery(
        {
          ...params,
          campaign_ids,
          user_ids,
        }
      )}`,
      switchProbeConfig()
    ),

  getWorkspaceAnalyticsDataHealth: (
    workspaceId: string,
    { campaign_ids, ...params }: GetWorkspaceAnalyticsDataHealthParamsI
  ) =>
    api.get<GetWorkspaceAnalyticsDataHealthResponseI>(
      `/v1/manage/workspace/${encodeURIComponent(workspaceId)}/analytics/data_health?${toWorkspaceAnalyticsQuery(
        { ...params, campaign_ids }
      )}`,
      switchProbeConfig()
    ),

  // Newer than the other two: a 404 also means a backend that doesn't have
  // the route yet, and the page then keeps the count-only tile.
  getWorkspaceAnalyticsMissingOutcomes: (
    workspaceId: string,
    {
      campaign_ids,
      page,
      page_size,
      ...params
    }: GetWorkspaceAnalyticsMissingOutcomesParamsI
  ) =>
    api.get<GetWorkspaceAnalyticsMissingOutcomesResponseI>(
      `/v1/manage/workspace/${encodeURIComponent(workspaceId)}/analytics/missing_outcomes?${toWorkspaceAnalyticsQuery(
        {
          ...params,
          campaign_ids,
          page: String(page),
          page_size: String(page_size),
        }
      )}`,
      switchProbeConfig()
    ),
});
